import { useCallback, useEffect, useRef, useState } from 'react'
import type { PostOptions } from '../api'
import { asList, asObj, mergeCatalogs, num, str, unwrap, type MediaCatalog, type Playable } from '../data'
import { useFanslyApi } from '../deps'
import { toFanslyError } from '../useFanslyQuery'
import { compareIds, messageOf, normalizeMessages, threadPageOf, threadQuery, type ChatGif, type ChatMessage } from './model'

/** The open chat asks for new messages this often. */
export const THREAD_POLL_MS = 20_000
/** Messages per page (the gateway's default). */
export const THREAD_PAGE = 50
/** A sent message without an id in Fansly's answer is matched to its echo within this time. */
const ECHO_MS = 180_000

export interface SendRequest {
  path: string
  body: Record<string, unknown>
}

export interface PendingMessage {
  tempId: string
  content: string
  /** What the bubble shows while it goes: the picked media and a GIF. */
  media: Playable[]
  gif: ChatGif | null
  voice: boolean
  /** Dollars (0 = free). */
  price: number
  /** sent = Fansly took it but did not answer with the message (its echo replaces it). */
  state: 'sending' | 'failed' | 'sent'
  error: string
  createdAt: number
  /** One idempotency key for the action: a retry (or the prohibited-wording resend) is the same send. */
  key: string
  /** Builds the request (uploads a voice note first); called again on retry. */
  prepare: () => Promise<SendRequest>
}

export type SendOutcome = 'sent' | 'failed' | 'cancelled'

type Run = <T>(write: (opts: PostOptions) => Promise<T>) => Promise<T | undefined>

interface Options {
  groupId: string
  fanId: string
  meId: string
  /** May write: acks, likes, deletes, sends. */
  canWrite: boolean
  /** The chat had unread messages when it opened: they are marked read once loaded. */
  unreadOnOpen: boolean
  /** Text writes through the prohibited-wording confirm (useProhibitedConfirm().run). */
  run: Run
  /** Something failed that the operator should hear about. */
  notify: (message: string) => void
  /** Something new happened in the chat (a send, a new message): the list refreshes its row. */
  onActivity?: () => void
}

let tempSeq = 0
export const newTempId = () => `tmp-${Date.now().toString(36)}-${(tempSeq += 1)}`
export const newSendKey = () => `fsc-send-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/**
 * One Fansly conversation (§7): the newest page, older pages on demand
 * (cursor `before` = oldest id with offset 0, or offset mode when Fansly
 * advertises `nextOffset`; the history ends when a page adds nothing), new
 * messages every 20 s (`after` = newest id), read acks with the groupId,
 * optimistic sends with retry, likes and deletes.
 */
export function useThread({ groupId, fanId, meId, canWrite, unreadOnOpen, run, notify, onActivity }: Options) {
  const api = useFanslyApi()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [pending, setPending] = useState<PendingMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [hasOlder, setHasOlder] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const messagesRef = useRef<ChatMessage[]>([])
  messagesRef.current = messages
  const pendingRef = useRef<PendingMessage[]>([])
  pendingRef.current = pending
  const catalog = useRef<MediaCatalog>({ media: new Map(), bundles: new Map() })
  const seq = useRef(0)
  const epoch = useRef(0)
  const resetting = useRef(true)
  const olderBusy = useRef(false)
  const pollBusy = useRef(false)
  const hasOlderRef = useRef(false)
  hasOlderRef.current = hasOlder
  /** Offset mode (only when Fansly advertised one) and an explicit cursor. */
  const nextOffset = useRef<number | null>(null)
  const cursor = useRef('')
  const ctx = useRef({ fanId, meId })
  ctx.current = { fanId, meId }
  const notifyRef = useRef(notify)
  notifyRef.current = notify
  const activityRef = useRef(onActivity)
  activityRef.current = onActivity

  const ack = useCallback(
    (list: ChatMessage[]) => {
      const ids = list.filter((m) => !m.mine && !m.deleted).map((m) => m.id)
      if (!canWrite || !ids.length) return
      // Fansly marks them read; the CRM needs the groupId to know whose chat this is (§15 #1).
      void api.post('/crm/messages/ack', { messageIds: ids.slice(-30), type: 2, groupId, _crm_fan_id: fanId }).catch(() => undefined)
    },
    [api, canWrite, groupId, fanId],
  )

  /** A server message now on screen replaces the pending send it echoes. */
  const dropEchoed = useCallback((list: ChatMessage[]) => {
    const now = Date.now()
    setPending((prev) =>
      prev.filter(
        (p) =>
          p.state !== 'sent' ||
          !list.some((m) => m.mine && Math.abs(m.createdAt - p.createdAt) < ECHO_MS && m.content.trim() === p.content.trim() && m.attachments.length > 0 === (p.media.length > 0 || p.voice)) ||
          now - p.createdAt > ECHO_MS * 2,
      ),
    )
  }, [])

  const merge = useCallback(
    (incoming: ChatMessage[]) => {
      const next = normalizeMessages([...messagesRef.current, ...incoming])
      messagesRef.current = next
      setMessages(next)
      dropEchoed(incoming)
      return next
    },
    [dropEchoed],
  )

  // The newest page (on open and on Reload).
  useEffect(() => {
    const mine = ++seq.current
    epoch.current += 1
    resetting.current = true
    setLoading(true)
    setError(null)
    api
      .get('/crm/messages', threadQuery(groupId, { kind: 'reset' }, THREAD_PAGE))
      .then((payload) => {
        if (seq.current !== mine) return
        const page = threadPageOf(payload, ctx.current)
        catalog.current = mergeCatalogs(catalog.current, page.catalog)
        nextOffset.current = page.nextOffset
        cursor.current = page.nextCursor
        const list = normalizeMessages(page.messages)
        // A reload keeps older pages already read: the newest page is merged in.
        const next = nonce ? normalizeMessages([...messagesRef.current, ...list]) : list
        messagesRef.current = next
        setMessages(next)
        setHasOlder(page.hasMoreFlag ?? page.received > 0)
        if (unreadOnOpen && !nonce) ack(list)
      })
      .catch((e: unknown) => seq.current === mine && setError(toFanslyError(e).message))
      .finally(() => {
        if (seq.current !== mine) return
        resetting.current = false
        setLoading(false)
      })
    // unreadOnOpen/ack: read once per open; ctx is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, groupId, nonce])

  const loadOlder = useCallback(() => {
    const list = messagesRef.current
    if (resetting.current || olderBusy.current || !hasOlderRef.current || !list.length) return
    olderBusy.current = true
    const started = epoch.current
    setLoadingOlder(true)
    const oldest = list.reduce((a, m) => (compareIds(m.id, a.id) < 0 ? m : a), list[0]!).id
    const query = threadQuery(groupId, { kind: 'older', before: cursor.current || oldest, offset: nextOffset.current }, THREAD_PAGE)
    api
      .get('/crm/messages', query)
      .then((payload) => {
        if (epoch.current !== started) return
        const page = threadPageOf(payload, { ...ctx.current, catalog: catalog.current })
        catalog.current = mergeCatalogs(catalog.current, page.catalog)
        const known = new Set(messagesRef.current.map((m) => m.id))
        const added = page.messages.filter((m) => !known.has(m.id))
        if (added.length) merge(added)
        nextOffset.current = page.nextOffset
        cursor.current = page.nextCursor
        // Fansly's pages are short: only an empty page, or one that adds nothing, ends the history.
        if (!page.received || !added.length || page.hasMoreFlag === false) setHasOlder(false)
      })
      .catch((e: unknown) => {
        if (epoch.current === started) notifyRef.current(toFanslyError(e).message)
      })
      .finally(() => {
        olderBusy.current = false
        setLoadingOlder(false)
      })
  }, [api, groupId, merge])

  const poll = useCallback(() => {
    if (resetting.current || pollBusy.current) return
    const list = messagesRef.current
    const newest = list.reduce<ChatMessage | null>((a, m) => (!a || compareIds(m.id, a.id) > 0 ? m : a), null)
    pollBusy.current = true
    const started = epoch.current
    api
      .get('/crm/messages', newest ? threadQuery(groupId, { kind: 'poll', after: newest.id }, THREAD_PAGE) : threadQuery(groupId, { kind: 'reset' }, THREAD_PAGE))
      .then((payload) => {
        if (epoch.current !== started) return
        const page = threadPageOf(payload, { ...ctx.current, catalog: catalog.current })
        catalog.current = mergeCatalogs(catalog.current, page.catalog)
        const known = new Set(messagesRef.current.map((m) => m.id))
        const added = page.messages.filter((m) => !known.has(m.id))
        // Likes and read state change on messages already shown: the page replaces them.
        merge(page.messages)
        if (added.length) {
          if (document.visibilityState !== 'hidden') ack(added)
          activityRef.current?.()
        }
      })
      .catch(() => undefined)
      .finally(() => {
        pollBusy.current = false
      })
  }, [api, groupId, merge, ack])

  useEffect(() => {
    const id = window.setInterval(() => document.visibilityState !== 'hidden' && poll(), THREAD_POLL_MS)
    return () => window.clearInterval(id)
  }, [poll])

  // ── Sending ────────────────────────────────────────────────────────────────

  const patchPending = useCallback((tempId: string, patch: Partial<PendingMessage> | null) => {
    setPending((prev) => (patch ? prev.map((p) => (p.tempId === tempId ? { ...p, ...patch } : p)) : prev.filter((p) => p.tempId !== tempId)))
  }, [])

  const deliver = useCallback(
    async (p: PendingMessage): Promise<SendOutcome> => {
      patchPending(p.tempId, { state: 'sending', error: '' })
      try {
        const req = await p.prepare()
        const answer = await run((opts) => api.postFull<Record<string, unknown>>(req.path, req.body, { ...opts, idempotencyKey: p.key }))
        if (answer === undefined) {
          patchPending(p.tempId, null)
          return 'cancelled'
        }
        const sent = asObj(unwrap(answer))
        const m = str(sent.id) ? messageOf(sent, { ...ctx.current, catalog: catalog.current }) : null
        // Media Fansly just created is not in any page yet: the next page brings the message with it.
        const sold = asList(sent.attachments).filter((a) => num(a.contentType) === 1 || num(a.contentType) === 2).length
        if (m && m.attachments.length >= sold) {
          merge([{ ...m, mine: true, createdAt: m.createdAt || Date.now() }])
          patchPending(p.tempId, null)
        } else {
          patchPending(p.tempId, { state: 'sent' })
        }
        activityRef.current?.()
        // Fansly's own copy (with its media) arrives with the next page.
        window.setTimeout(poll, 1200)
        return 'sent'
      } catch (e) {
        patchPending(p.tempId, { state: 'failed', error: toFanslyError(e).message })
        return 'failed'
      }
    },
    [api, run, merge, patchPending, poll],
  )

  const send = useCallback(
    (p: Omit<PendingMessage, 'tempId' | 'state' | 'error' | 'createdAt' | 'key'>) => {
      const full: PendingMessage = { ...p, tempId: newTempId(), state: 'sending', error: '', createdAt: Date.now(), key: newSendKey() }
      setPending((prev) => [...prev, full])
      return deliver(full)
    },
    [deliver],
  )

  const retry = useCallback(
    (tempId: string) => {
      const p = pendingRef.current.find((x) => x.tempId === tempId)
      if (p) void deliver(p)
    },
    [deliver],
  )

  const discard = useCallback((tempId: string) => patchPending(tempId, null), [patchPending])

  // ── Message actions ────────────────────────────────────────────────────────

  const replace = useCallback((id: string, update: (m: ChatMessage) => ChatMessage | null) => {
    const next = messagesRef.current.flatMap((m) => {
      if (m.id !== id) return [m]
      const u = update(m)
      return u ? [u] : []
    })
    messagesRef.current = next
    setMessages(next)
  }, [])

  const like = useCallback(
    async (m: ChatMessage, liked: boolean) => {
      const before = m
      replace(m.id, (x) => ({
        ...x,
        likes: liked ? [...x.likes, { id: `local-${x.id}`, accountId: ctx.current.meId, type: 1 }] : x.likes.filter((l) => !(l.type === 1 && l.accountId !== ctx.current.fanId)),
      }))
      try {
        await api.post(liked ? '/crm/messages/like' : '/crm/messages/unlike', { messageId: m.id, groupId, _crm_fan_id: fanId })
      } catch (e) {
        replace(m.id, () => before)
        notifyRef.current(toFanslyError(e).message)
      }
    },
    [api, groupId, fanId, replace],
  )

  const remove = useCallback(
    async (m: ChatMessage) => {
      const before = messagesRef.current
      replace(m.id, () => null)
      try {
        await api.post('/crm/messages/delete', { messageId: m.id, groupId, confirm: true, _crm_fan_id: fanId })
        activityRef.current?.()
      } catch (e) {
        messagesRef.current = before
        setMessages(before)
        notifyRef.current(toFanslyError(e).message)
      }
    },
    [api, groupId, fanId, replace],
  )

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { messages, pending, loading, loadingOlder, hasOlder, error, loadOlder, poll, reload, send, retry, discard, like, remove }
}

export type Thread = ReturnType<typeof useThread>
